import { ChangeEvent, useRef, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { Alert, Button, Spinner, Table } from 'react-bootstrap';

import {
  GetPushBroadcastTranslationTemplateQuery,
  GetPushBroadcastTranslationTemplateQueryVariables,
  PreviewPushBroadcastTranslationImportMutation,
  PreviewPushBroadcastTranslationImportMutationVariables,
  UpdatePushBroadcastMutation,
  UpdatePushBroadcastMutationVariables,
} from '../../../../../../__gqltypes__';
import {
  GET_PUSH_BROADCAST_TRANSLATION_TEMPLATE,
  PREVIEW_PUSH_BROADCAST_TRANSLATION_IMPORT,
  UPDATE_PUSH_BROADCAST,
} from '../graphql';
import {
  base64ToBlob,
  groupPushImportChanges,
  pushBroadcastErrorIssues,
  PushBroadcastData,
  pushBroadcastToDraft,
  pushDraftToInput,
  pushFileSlug,
  PushIssue,
  PushBroadcastLanguageOption,
  pushLanguageName,
} from '../utils';
import { SolidBadge, SolidBadgeTone } from '../../../../../../utils/components/SolidBadge';
import { PushIssueList } from './PushIssueList';

type ImportPreview = PreviewPushBroadcastTranslationImportMutation['previewPushBroadcastTranslationImport'];

type Props = {
  broadcast: PushBroadcastData;
  languages: ReadonlyArray<PushBroadcastLanguageOption>;
  // Why the spreadsheet cannot be used right now (unsaved edits, not a draft), null when it can.
  blockedReason: string | null;
  onApplied: (broadcast: PushBroadcastData) => void;
};

const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const STATUS_LABEL: Record<'new' | 'removed' | 'changed', { tone: SolidBadgeTone; text: string }> = {
  new: { tone: 'green', text: 'new language' },
  removed: { tone: 'red', text: 'removed: will receive English' },
  changed: { tone: 'teal', text: 'changed' },
};

/**
 * Translations by spreadsheet, for many languages at once: download the pre-filled template, upload it back, see the
 * diff and every error of the file before anything is saved. Inline editing below stays available for a small fix.
 */
export function PushTranslationsXlsx({ broadcast, languages, blockedReason, onApplied }: Props): JSX.Element {
  const client = useApolloClient();
  const [downloading, setDownloading] = useState(false);
  const [issues, setIssues] = useState<PushIssue[]>([]);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  // What just worked (a download, an applied import): every action ends with something visible.
  const [notice, setNotice] = useState<string | null>(null);
  // Remounts the file input, so the same file can be picked again after a fix.
  const [inputKey, setInputKey] = useState(0);
  const fileInput = useRef<HTMLInputElement>(null);

  const [previewImport, { loading: reading }] = useMutation<
    PreviewPushBroadcastTranslationImportMutation,
    PreviewPushBroadcastTranslationImportMutationVariables
  >(PREVIEW_PUSH_BROADCAST_TRANSLATION_IMPORT);
  const [updateBroadcast, { loading: applying }] = useMutation<
    UpdatePushBroadcastMutation,
    UpdatePushBroadcastMutationVariables
  >(UPDATE_PUSH_BROADCAST);

  const download = async () => {
    setIssues([]);
    setNotice(null);
    setDownloading(true);
    try {
      const { data } = await client.query<
        GetPushBroadcastTranslationTemplateQuery,
        GetPushBroadcastTranslationTemplateQueryVariables
      >({ query: GET_PUSH_BROADCAST_TRANSLATION_TEMPLATE, variables: { id: broadcast.id }, fetchPolicy: 'no-cache' });
      if (!data?.pushBroadcastTranslationTemplate) {
        setIssues([{ message: 'The server returned an empty template: nothing was downloaded.' }]);
        return;
      }
      const filename = `${pushFileSlug(broadcast.name)}-translations.xlsx`;
      const url = URL.createObjectURL(base64ToBlob(data.pushBroadcastTranslationTemplate, XLSX_TYPE));
      // The admin's download pattern (xlsxExport, Loyalty Point Mapping export): the link is in the page when
      // clicked, which some browsers require.
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setNotice(`Template downloaded as ${filename}. If no file appeared, check your browser's downloads.`);
    } catch (error) {
      setIssues(pushBroadcastErrorIssues(error));
    } finally {
      setDownloading(false);
    }
  };

  const upload = async (file: File | undefined) => {
    setInputKey((key) => key + 1);
    if (!file) {
      return;
    }
    setIssues([]);
    setNotice(null);
    setPreview(null);
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      setIssues([
        { message: `"${file.name}" is not an .xlsx file. Download the template, fill it in, and upload that file.` },
      ]);
      return;
    }
    try {
      const result = await previewImport({ variables: { id: broadcast.id, file } });
      const read = result.data?.previewPushBroadcastTranslationImport ?? null;
      if (!read) {
        setIssues([{ message: `The server returned no result for "${file.name}": nothing was imported.` }]);
        return;
      }
      setPreview(read);
    } catch (error) {
      setIssues(pushBroadcastErrorIssues(error));
    }
  };

  const apply = async () => {
    if (!preview?.ok) {
      return;
    }
    setIssues([]);
    try {
      const result = await updateBroadcast({
        variables: {
          id: broadcast.id,
          // The revision the diff was computed against: a save in between is a conflict, never overwritten.
          expectedVersion: preview.expectedVersion,
          input: {
            ...pushDraftToInput(pushBroadcastToDraft(broadcast)),
            titleTexts: preview.titleTexts,
            bodyTexts: preview.bodyTexts,
          },
        },
      });
      const updated = result.data?.updatePushBroadcast;
      if (updated) {
        setPreview(null);
        const changed = groupPushImportChanges(preview.changes, broadcast, languages).length;
        setNotice(`Import applied and saved: ${changed} language(s) changed.`);
        onApplied(updated);
      } else {
        setIssues([
          { message: 'The server returned no broadcast: the import may not have been saved. Reload to check.' },
        ]);
      }
    } catch (error) {
      setIssues(pushBroadcastErrorIssues(error));
    }
  };

  const groups = preview ? groupPushImportChanges(preview.changes, broadcast, languages) : [];
  const disabled = !!blockedReason;

  return (
    // A part of the editor's "Notification text" section, not a card of its own.
    <section>
      <h6 className="font-weight-bold mb-2">Translations by spreadsheet</h6>
      <div className="d-flex flex-wrap align-items-center mb-2">
        <Button
          size="sm"
          variant={disabled ? 'outline-secondary' : 'outline-primary'}
          className="mr-2"
          disabled={disabled || downloading}
          onClick={download}
        >
          {disabled ? <i className="fas fa-lock mr-1" aria-hidden="true" /> : null}
          {downloading ? <Spinner animation="border" size="sm" className="mr-1" /> : null}
          Download the template (.xlsx)
        </Button>
        {/* The admin's spreadsheet import pattern (Loyalty Point Mapping "Import XLSX"): a hidden file input opened
            by an outline button, themed in both modes, instead of the custom file control. */}
        <input
          key={inputKey}
          ref={fileInput}
          id="push-translations-upload"
          type="file"
          accept=".xlsx"
          style={{ display: 'none' }}
          onChange={(event: ChangeEvent<HTMLInputElement>) => upload(event.target.files?.[0])}
        />
        <Button
          size="sm"
          variant={disabled ? 'outline-secondary' : 'outline-primary'}
          disabled={disabled || reading || applying}
          onClick={() => fileInput.current?.click()}
        >
          {disabled ? <i className="fas fa-lock mr-1" aria-hidden="true" /> : null}
          {reading ? 'Reading the file…' : 'Upload the filled template…'}
        </Button>
        {/* Off and why, read together: the reason sits right after the two buttons it locks. */}
        {blockedReason ? (
          <span className="small ml-2">
            <span className="font-weight-bold">The spreadsheet is locked:</span> {blockedReason}
          </span>
        ) : null}
      </div>
      {blockedReason ? null : (
        <>
          <small className="text-muted d-block">
            The template is filled from the saved English text, so write and save that first.
          </small>
          <small className="text-muted d-block mb-2">
            Pre-filled with every language and the saved texts. Nothing is saved until you apply the import below.
          </small>
        </>
      )}
      {notice ? (
        <Alert variant="success" className="py-2 mb-2">
          {notice}
        </Alert>
      ) : null}
      <PushIssueList issues={issues} title="The spreadsheet could not be used" />

      {preview ? (
        <div className="border rounded p-2">
          <PushIssueList
            issues={preview.errors}
            title="The file has errors, nothing was applied. Fix them all, then upload again"
          />
          <PushIssueList issues={preview.warnings} title="Warnings (not blocking)" variant="warning" />
          {preview.ok && groups.length === 0 ? <div className="text-muted">The file changes nothing.</div> : null}
          {groups.length > 0 ? (
            <Table size="sm" className="mb-2">
              <thead>
                <tr>
                  <th>Language</th>
                  <th>Field</th>
                  <th>Before</th>
                  <th>After</th>
                </tr>
              </thead>
              <tbody>
                {groups.flatMap(({ language, status, changes }) =>
                  changes.map((change, index) => (
                    <tr key={`${language}-${change.field}`}>
                      <td>
                        {index === 0 ? (
                          <>
                            <div className="font-weight-bold">{pushLanguageName(language, languages)}</div>
                            <SolidBadge tone={STATUS_LABEL[status].tone}>{STATUS_LABEL[status].text}</SolidBadge>
                          </>
                        ) : null}
                      </td>
                      <td>{change.field}</td>
                      <td className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>
                        {change.before ?? '—'}
                      </td>
                      <td style={{ whiteSpace: 'pre-wrap' }}>{change.after ?? '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          ) : null}
          <div className="d-flex">
            {preview.ok && groups.length > 0 ? (
              <Button size="sm" className="mr-2" disabled={applying || disabled} onClick={apply}>
                {applying ? <Spinner animation="border" size="sm" className="mr-1" /> : null}
                Apply these changes
              </Button>
            ) : null}
            <Button size="sm" variant="outline-secondary" onClick={() => setPreview(null)}>
              Discard the import
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
